import { useState } from 'react';
import { Button, Modal, Notice, Spinner } from '../ui';
import { Link } from '../router/Link';
import { CreatePlaylistForm } from '../playlists/CreatePlaylistForm';
import { signInHereHref } from '../playlists/model';
import { useSaveQueue } from './useSaveQueue';
import type { SaveTargetRow } from './saveModel';
import { useCopy } from '../strings/useCopy';
import styles from '../playlists/addToPlaylist.module.css';

/**
 * Save the whole queue into one of the reader's own playlists — the panel's sixth control while the list
 * is this tab's own, standing where Unlink stands while it is a playlist's.
 *
 * It is the answer to the one thing the queue never said anywhere on screen: the list belongs to this
 * browser tab and closing the tab is the end of it. That sentence is the control's `title`, on the
 * control that does something about it.
 *
 * **`AddToPlaylist`'s clothes**, down to its stylesheet, its row shape and its create form at the
 * bottom — the third dialog in the app to ask "which of your playlists", and a reader who has seen one
 * has seen the others. Its reasons carry over unchanged: a `Modal` and not a `Menu`, because a popover
 * closing on the first click inside it would take away the answers the expensive list paid for, and the
 * list is fetched **when the dialog opens** rather than on mount, since the response is roughly 1.2 KB
 * per entry across every playlist the reader owns.
 *
 * It renders its trigger **signed out** as well, unlike the same dialog's face on a grid card: this is
 * one control on the panel rather than one per card, the queue itself needs no account, and a reader who
 * has built a list and wants to keep it is exactly who the sign-in arm is for.
 */
export function SaveQueueToPlaylist({
  className,
  onSaved,
  signInHref,
}: {
  /** The panel's own control class, so this button measures like the five beside it. */
  readonly className?: string;
  /** The queue is that playlist's now — the address takes the link up (`useWatchScreen`). */
  readonly onSaved: (playlistId: string, index: number | null) => void;
  /** `/login?next=<this video>` — built by the caller, so no href is assembled in a `.tsx` (R13). */
  readonly signInHref?: string;
}) {
  const [open, setOpen] = useState(false);
  const words = useCopy().text.watch.queue.save;
  const flow = useSaveQueue({
    open,
    onSaved: (playlistId, index) => {
      setOpen(false);
      onSaved(playlistId, index);
    },
  });

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        icon
        className={className}
        label={words.label}
        title={words.detail}
        onClick={() => setOpen(true)}
      >
        <SaveGlyph />
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title={words.title} closeLabel={words.close}>
        {!flow.signedIn ? (
          <p className={styles.detail}>
            {words.signedOutBefore}{' '}
            <Link className={styles.link} href={signInHref ?? signInHereHref()}>
              {words.signIn}
            </Link>{' '}
            {words.signedOutAfter}
          </p>
        ) : (
          <div className={styles.content}>
            {/* What the press will do, before it is pressed: which videos go across, and that the
                playlist's order is what the list plays in afterwards. */}
            <p className={styles.detail}>{words.lede}</p>

            {flow.loading ? (
              // A `<div>` rather than a `<p>`: `Spinner` renders a `<div>`, which is invalid inside one
              // and reported as a hydration error (docs/specs/web-ui.md).
              <div className={styles.detail} role="status">
                <Spinner />
                <span>{words.loading}</span>
              </div>
            ) : null}

            {flow.loadProblem === null ? null : (
              <Notice tone="caution">
                <strong>{flow.loadProblem.title}.</strong> {flow.loadProblem.detail}
              </Notice>
            )}

            {/* No jump-out to the Playlists page: the create form is right below this sentence. */}
            {flow.rows !== null && flow.rows.length === 0 ? (
              <p className={styles.detail}>{words.none}</p>
            ) : null}

            {flow.rows === null || flow.rows.length === 0 ? null : (
              <ul className={styles.list}>
                {flow.rows.map((row) => (
                  <Target key={row.playlistId} row={row} onSave={flow.save} />
                ))}
              </ul>
            )}

            {/* A refusal is said here as well as spoken, because it is the one outcome that leaves the
                page exactly as it was: a save that worked turns the panel into the playlist. */}
            {flow.problem === null ? null : (
              <Notice tone="caution" live>
                {flow.problem}
              </Notice>
            )}

            {/*
             * Creating from here saves the queue into the new playlist, which is the case this control
             * was really for: a fresh playlist holds nothing, so it ends up in exactly the queue's order.
             */}
            <CreatePlaylistForm
              id="save-queue-create"
              submitLabel={words.createSubmit}
              onCreated={flow.save}
            />
          </div>
        )}
      </Modal>
    </>
  );
}

function Target({
  row,
  onSave,
}: {
  readonly row: SaveTargetRow;
  readonly onSave: (playlistId: string, playlistTitle: string) => void;
}) {
  const words = useCopy().text.watch.queue.save;

  return (
    <li className={styles.row}>
      <button
        type="button"
        className={styles.rowButton}
        disabled={row.disabled}
        onClick={() => onSave(row.playlistId, row.title)}
      >
        <span className={styles.rowTitle}>{row.title}</span>
        <span className={styles.rowMeta}>
          {row.countText === null ? null : <span>{row.countText}</span>}
        </span>
        <span className={styles.rowState} aria-live="polite">
          {row.state === 'idle' ? null : <Spinner />}
          {/* Two words rather than one: reading a long playlist out comes before anything is sent, and
              it is the longer half of the wait on a list of hundreds. */}
          {row.state === 'reading' ? <span>{words.reading}</span> : null}
          {row.state === 'saving' ? <span>{words.saving}</span> : null}
        </span>
      </button>
    </li>
  );
}

/**
 * Playlist lines gaining a plus — Save to playlist's own picture, redrawn here the way every glyph in
 * this app lives with the component that draws it.
 *
 * The reason that picture is avoided on a grid **card** does not apply on this row: there, Save to
 * playlist sits two glyphs above the queue button in the same stack, so two lists with a plus would
 * make the pair a coin toss. Here there is no such neighbour, and the list with a plus is the clearest
 * thing "put this in a playlist" can be drawn as.
 */
function SaveGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M4 6.5h12M4 11h12M4 15.5h7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.5 13.5v5M15 16h5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
